import React, { Component, Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { OrderedMap, Set, Map } from 'immutable';
import cx from 'classnames';

const propTypes = {
  children: PropTypes.node.isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  requiredFieldErrors: PropTypes.oneOfType([
    PropTypes.instanceOf(OrderedMap),
    PropTypes.instanceOf(Map),
  ]).isRequired,
  requiredFields: PropTypes.instanceOf(Set).isRequired,
  shouldScrollToError: PropTypes.bool,
  setShouldScrollToError: PropTypes.func,
};

const defaultProps = {
  shouldScrollToError: false,
  setShouldScrollToError: () => {},
};

class RequiredField extends Component {
  static _isMaxScrollLimitReached() {
    // Take max of all window values for browser compatability purposes
    const maxHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight,
                               document.documentElement.clientHeight, document.documentElement.scrollHeight,
                               document.documentElement.offsetHeight);
    const limit = maxHeight - window.innerHeight;
    return !!(window.scrollY === limit);
  }

  constructor(props) {
    super(props);

    this._addClassesToChildren = this._addClassesToChildren.bind(this);
    this._scrollToError = this._scrollToError.bind(this);
  }

  componentDidMount() {
    // Get required input id and add it to ordered list
    this.props.modifyRequiredFieldList({ add: this._getRequiredField() });
  }

  componentDidUpdate(prevProps) {
    const { shouldScrollToError, requiredFieldErrors } = this.props;

    if (!shouldScrollToError || prevProps.shouldScrollToError) { return; }

    this._scrollToError();

    // Disable scroll functionality for further validations
    if (requiredFieldErrors.size) {
      this.props.setShouldScrollToError({ shouldScrollToError: false });
    }
  }

  componentWillUnmount() {
    this.props.modifyRequiredFieldList({ remove: this._getRequiredField() });
  }

  _scrollToError() {
    // First confirm error exists
    const field = this._getRequiredField();
    const { requiredFieldErrors } = this.props;
    if (!field || !requiredFieldErrors || !requiredFieldErrors.get(field)) { return; }

    // Verify that new errors exist
    // Verify that this is the highest error dom element
    // If so, scroll to element, if not, return
    const highestError = this._getHighestError();
    if (highestError !== field) { return; }

    this._errorPopup.scrollIntoView();

    // Currently, the header is blocking the scroll, so we'll want to scroll
    // based on it's offset height as well.  If window is already max-scrolled, however, return
    if (RequiredField._isMaxScrollLimitReached()) { return; }
    const header = document.getElementById('main-top-nav');
    if (!header) { return; }
    const yOffsetScroll = window.scrollY - header.offsetHeight;
    if (isNaN(yOffsetScroll)) { return; }
    window.scroll(0, yOffsetScroll);
  }

  _shouldComponentScroll(prevProps) {
    const { requiredFields, requiredFieldErrors } = this.props;
    const { prevRequiredFields, prevRequiredFieldErrors } = prevProps;

    // Only scroll if new errors are present
    if (!prevRequiredFields || !prevRequiredFieldErrors) { return true; }
    if (requiredFields &&
        prevRequiredFields &&
        !requiredFields.equals(prevRequiredFields) &&
        prevRequiredFields.size < requiredFields.size) {
      return true;
    }
    if (requiredFieldErrors &&
        prevRequiredFieldErrors &&
        !requiredFieldErrors.equals(prevRequiredFieldErrors) &&
        prevRequiredFieldErrors.size < requiredFieldErrors.size) {
      return true;
    }
    return false;
  }

  _getHighestError() {
    const { requiredFields, requiredFieldErrors } = this.props;
    if (!requiredFields || !requiredFieldErrors) { return null; }

    let highestError = null;
    let highestErrorIndex = Number.MAX_SAFE_INTEGER;
    for (const field of requiredFieldErrors.keys()) {
      const i = requiredFields.toList().indexOf(field);
      if (i !== -1 && i <= highestErrorIndex) {
        highestError = field;
        highestErrorIndex = i;
      }
    }

    return highestError;
  }

  _getRequiredField() {
    return this.props.children.props['data-field-name'];
  }

  _addClassesToChildren(child) {
    const { requiredFieldErrors } = this.props;
    const errorField = requiredFieldErrors ? requiredFieldErrors.get(this._getRequiredField()) : false;
    const isPotentiallyValid = (errorField || { isPotentiallyValid: true }).isPotentiallyValid;

    let childClasses = cx({
      'form-control-error': errorField,
      'braintree-hosted-fields-invalid': !isPotentiallyValid,
    });
    childClasses = `${childClasses} ${child.props.className}`;
    return cloneElement(child, { className: childClasses });
  }

  _renderEmptyFieldError(fieldName) {
    const capitalFieldName = fieldName ? fieldName[0].toUpperCase() + fieldName.slice(1) : null;
    return (<div ref={(e) => { this._errorPopup = e; return this._errorPopup; }} className="error-popup">{capitalFieldName} is required</div>);
  }

  _renderInvalidFieldError(fieldName) {
    return (<div ref={(e) => { this._errorPopup = e; return this._errorPopup; }} className="error-popup">Invalid {fieldName}</div>);
  }

  render() {
    const { requiredFieldErrors, children } = this.props;
    const field = this._getRequiredField();
    let error = null;

    if (field && requiredFieldErrors && requiredFieldErrors.get(field)) {
      const requiredFieldError = requiredFieldErrors.get(field);
      if (requiredFieldError.isEmpty) {
        error = this._renderEmptyFieldError(requiredFieldError.displayName || requiredFieldError.get('displayName'));
      } else if (!requiredFieldError.isValid) {
        error = this._renderInvalidFieldError(requiredFieldError.displayName || requiredFieldError.get('displayName'));
      }
    }

    return (
      <div className="form-field-container">
        { error }
        { Children.map(children, this._addClassesToChildren) }
      </div>
    );
  }
}

RequiredField.propTypes = propTypes;
RequiredField.defaultProps = defaultProps;

export default RequiredField;
